<template>
  <div
    v-if="!options.hideHeaderOnMobile || breakpoints.greaterOrEqual('md').value"
    class="w-screen z-[10000] m-0 flex-grow-0 flex-shrink-0"
    :style="{ height: headerHeight + 'px' }"
  >
    <HeaderBackground
      v-if="options.blueTheme && breakpoints.smaller('md').value"
      class="fixed z-[800000] bottom-[calc(100vh-184px)] left-1/2 w-screen min-w-[375px] min-h-[184px] transform translate-x-[-50%] pointer-events-none"
    />
    <HeaderBackgroundDesktop
      v-if="options.blueTheme && breakpoints.greaterOrEqual('md').value"
      class="fixed z-[800000] bottom-[calc(100vh-509px)] xl:top-0 xl:bottom-auto left-1/2 w-screen min-w-[1440px] min-h-[509px] transform translate-x-[-50%] pointer-events-none"
    />
    <header
      class="fixed top-0 w-full z-[800100] px-4 xl:px-[170px]"
      :class="{
        'h-auto': !options.blueTheme,
        'bg-white': !options.blueTheme,
      }"
      :style="{ height: headerHeight + 'px' }"
    >
      <div class="flex flex-row h-[56px] md:h-[84px] mx-4 md:mx-8 my-0 items-center justify-between">
        <div class="ml-1 grow-0">
          <RouterLink v-if="!options.showBackButton" :to="{ name: 'home' }">
            <Logo
              class="object-contain h-[24px] sm:h-[32px] md:h-[52px]"
              :blue-theme="options.blueTheme"
              :size="breakpoints.smaller('sm').value ? LogoSize.SMALL : (breakpoints.smaller('md').value ? LogoSize.MEDIUM : LogoSize.DEFAULT)"
            />
          </RouterLink>

          <BackButton v-if="options.showBackButton" :toUrl="options.backUrl" />
        </div>
        <div class="hidden lg:flex flex-grow max-w-[1500px]  justify-center">
          <RouterLink :to="{ name: 'home' }" class="p-4 text-[#255CA5] text-sm font-light" exact-active-class="!font-bold">
            {{ t('header.home') }}
          </RouterLink>
          <RouterLink :to="{ name: 'products' }" class="p-4 text-[#255CA5] text-sm font-light" activeClass="!font-bold">
            {{ t('header.products') }}
          </RouterLink>
          <RouterLink :to="{ name: 'vision' }" class="p-4 text-[#255CA5] text-sm font-light" activeClass="!font-bold">
            {{ t('header.vision') }}
          </RouterLink>
          <RouterLink :to="{ name: 'team' }" class="p-4 text-[#255CA5] text-sm font-light" activeClass="!font-bold">
            {{ t('header.about') }}
          </RouterLink>
        </div>
        <div class="hidden lg:block">
          <VaButton
            color="blue-500"
            round
            :to="{ name: 'contact' }"
          >
            <span class="py-2 px-[6px] text-[14px] whitespace-nowrap">{{ t('header.contactUs') }}</span>
          </VaButton>
        </div>
        <div class="grow" />
        <button class="p-2" @click="menuBtnTapped">
          <MenuIcon />
        </button>
      </div>
      <p v-if="options.blueTheme && options.title" class="text-center text-white text-xl font-bold mt-6 md:mt-0">{{ t(options.title) }}</p>
      <slot />
    </header>

    <SideMenu />
  </div>
</template>

<script lang="ts" setup>
import { VaButton } from 'vuestic-ui'
import BackButton from '@/components/BackButton.vue'
import HeaderBackground from '@/assets/images/header_background.svg'
import HeaderBackgroundDesktop from '@/assets/images/header_background_desktop.svg'
import MenuIcon from '@/assets/icons/new-icon.svg'
import { LogoSize } from '@/components/ui'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router';
import SideMenu from '@/components/side-menu/SideMenu.vue'
import { EventBus } from '@/services/event-bus'
import { Logo } from '@/components/ui/logo/Logo'

const { t } = useI18n()
const breakpoints = useBreakpoints(breakpointsTailwind)
const route = useRoute()
const searchText = ref('')

class HeaderOptions {
  blueTheme = false
  showBackButton = false
  backUrl = ''
  title = ''
  showSearchBar = false
  hideButtons = false
  hideHeaderOnMobile = false
}

const options = reactive<HeaderOptions>(new HeaderOptions())

const menuBtnTapped = (): Promise<void> => {
  EventBus.dispatch('openSideMenu')
  return Promise.resolve()
}

const headerHeight = computed((): number => {
  if (breakpoints.greaterOrEqual('md').value) {
    return options.blueTheme ? 160 : 84
  } else {
    return options.blueTheme ? 185 : 56
  }
})

const headerOptions = computed(() => {
  return route.matched.slice().reverse().find((record) => {
    return record.meta.header
  })?.meta.header
})

watch(() => route.fullPath, () => {
  searchText.value = ''
})

watch(headerOptions, (value) => {
  const newOptions = new HeaderOptions()
  if (value) {
    Object.assign(newOptions, value)
  }
  options.blueTheme = newOptions.blueTheme
  options.showBackButton = newOptions.showBackButton
  options.backUrl = newOptions.backUrl
  options.title = newOptions.title
  options.showSearchBar = newOptions.showSearchBar
  options.hideButtons = newOptions.hideButtons
  options.hideHeaderOnMobile = newOptions.hideHeaderOnMobile
}, { immediate: true })
</script>